'use client'

import React, { useEffect, useRef } from 'react'
import { Loader2, Pause, Play, RotateCcw, Square } from 'lucide-react'

// The "···" replay button + popover from SIBS's Custom Mail Alert header
// (SibsMailView.tsx, CampaignsView), same markup and states, for dashboards
// that own their own replay state machine (CTT Vendor Entries).
export type ReplayStatus = 'idle' | 'loading' | 'playing' | 'paused' | 'done'

function isoToPtDate(iso: string) {
  const [year, month, day] = iso.split('-')
  return year && month && day ? `${day}/${month}/${year}` : iso
}

export function ReplayControl({
  status,
  open,
  onOpenChange,
  from,
  to,
  rate,
  onRateChange,
  index,
  queueLength,
  onPlay,
  onPause,
  onResume,
  onStop,
  isDark,
  border,
  muted,
}: {
  status: ReplayStatus
  open: boolean
  onOpenChange: (open: boolean) => void
  from: string
  to: string
  rate: number
  onRateChange: (rate: number) => void
  index: number
  queueLength: number
  /** Starts (or restarts) the replay from the beginning. */
  onPlay: () => void
  onPause: () => void
  onResume: () => void
  onStop: () => void
  isDark: boolean
  border: string
  muted: string
}) {
  const ref = useRef<HTMLDivElement | null>(null)
  const isReplaying = status !== 'idle'

  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onOpenChange(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open, onOpenChange])

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => {
          if (status === 'loading') return
          if (status === 'playing') {
            onPause()
            onOpenChange(false)
            return
          }
          onOpenChange(!open)
        }}
        title={status === 'loading' ? 'Loading replay' : status === 'playing' ? 'Pause replay' : 'Replay'}
        className={`flex h-7 w-7 items-center justify-center border font-mono text-[15px] leading-none transition-colors ${
          open || isReplaying
            ? 'border-warning-border text-warning bg-warning-surface'
            : `${border} ${muted} hover:border-warning-border hover:text-warning`
        }`}
      >
        {status === 'loading'
          ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
          : status === 'playing'
            ? <Pause className="h-3.5 w-3.5" />
            : '···'}
      </button>

      {open && status !== 'loading' && status !== 'playing' && (
        <div className={`absolute right-0 top-full z-30 mt-1 w-72 rounded border p-4 shadow-notice bg-card border-border`}>
          <div className={`font-mono text-[9px] uppercase tracking-widest mb-3 ${muted}`}>// Replay</div>

          <div className="flex flex-col gap-2 mb-3">
            <div className={`font-mono text-[10px] px-1 ${muted}`}>
              {isoToPtDate(from)} → {isoToPtDate(to)}
            </div>
            <div className="flex items-center gap-2">
              <span className={`font-mono text-[9px] w-7 shrink-0 uppercase tracking-widest ${muted}`}>Rate</span>
              <input type="number" min={1} value={rate}
                onChange={e => onRateChange(Math.max(1, Number(e.target.value)))}
                className={`w-16 h-7 border px-2 font-mono text-[11px] text-center ${border} bg-card text-muted-foreground`}
              />
              <span className={`font-mono text-[10px] ${muted}`}>/s</span>
            </div>
          </div>

          <div className={`border-t mb-3 ${border}`} />

          <div className="flex items-center gap-2">
            {status === 'idle' && (
              <button onClick={onPlay}
                className="flex h-7 flex-1 items-center justify-center gap-1.5 border border-warning-border px-3 font-mono text-[10px] uppercase tracking-widest text-warning transition-colors hover:bg-warning-surface"
              ><Play className="h-3 w-3" /> Play</button>
            )}
            {status === 'paused' && (<>
              <button onClick={onResume}
                className="flex h-7 items-center gap-1.5 border border-warning-border px-3 font-mono text-[10px] uppercase tracking-widest text-warning transition-colors hover:bg-warning-surface"
              ><Play className="h-3 w-3" /> Resume</button>
              <button onClick={onPlay}
                className="flex h-7 items-center gap-1.5 border border-warning-border px-3 font-mono text-[10px] uppercase tracking-widest text-warning transition-colors hover:bg-warning-surface"
              ><RotateCcw className="h-3 w-3" /> Replay</button>
              <button onClick={onStop}
                className={`flex h-7 items-center gap-1.5 border px-3 font-mono text-[10px] uppercase tracking-widest transition-colors hover:border-destructive hover:text-destructive ${border} ${muted}`}
              ><Square className="h-3 w-3" /> Stop</button>
            </>)}
            {status === 'done' && (
              <button onClick={onPlay}
                className="flex h-7 items-center gap-1.5 border border-success px-3 font-mono text-[10px] uppercase tracking-widest text-success transition-colors hover:bg-success-surface"
              ><Play className="h-3 w-3" /> Play again</button>
            )}
          </div>

          {(status === 'paused' || status === 'done') && (
            <div className="mt-3 flex items-center gap-2">
              <div className={`flex-1 h-0.5 rounded-full bg-secondary`}>
                <div
                  className={`h-0.5 rounded-full transition-all ${status === 'done' ? 'bg-success' : 'bg-warning'}`}
                  style={{ width: `${queueLength > 0 ? (index / queueLength) * 100 : 0}%` }}
                />
              </div>
              <span className={`font-mono text-[10px] tabular-nums shrink-0 ${status === 'done' ? 'text-success' : muted}`}>
                {status === 'done' ? '✓ done' : `${index} / ${queueLength}`}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
